import React, { useEffect, useRef, useState } from "react";
import "./Navbar.css";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import { FaShieldAlt, FaUsers, FaTruck, FaUser, FaCartPlus } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import logo from "../../Assets/Banner/swasthmitra_logo.png";
import df from "../../Assets/Brands/default.png";
import { clearUser } from "../../Features/User/UserSlice";
import { clearCart } from "../../Features/Cart/cartSlice";

const searchSuggestions = [
  "Multivitamin", "Skin Care", "Medicines", "Protein Powder",
  "Iron", "Calcium", "Liver Care", "Ayurveda", "Homeopathy", "Medical Device",
];

const Navbar = () => {
  const user = useSelector((s) => s.user.user);
  const cart = useSelector((s) => s.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isHomePage = location.pathname === "/";

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    setIsLoggedIn(!!user);
  }, [user]);

  useEffect(() => {
    const id = setInterval(
      () => setPlaceholderIndex((i) => (i + 1) % searchSuggestions.length),
      2500
    );
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const fetchSuggestions = async (value) => {
    if (!value.trim()) {
      setSuggestions([]);
      return;
    }

    try {
      const res = await axios.get("https://api.swasthmitra.com/api/medicine");
      const v = value.toLowerCase();

      setSuggestions(
        res.data
          .filter((m) =>
            m.name?.toLowerCase().includes(v) ||
            m.brand?.toLowerCase().includes(v) ||
            m.salt?.toLowerCase().includes(v)
          )
          .slice(0, 7)
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchSuggestions(value);
    setShowSuggestions(true);
  };

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchTerm)}`);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (id) => {
    navigate(`/product/${id}`);
    setShowSuggestions(false);
    setSearchTerm("");
  };

  const handleLogout = () => {
    localStorage.clear();
    dispatch(clearUser());
    dispatch(clearCart());
    setDropdownOpen(false);
    navigate("/");
  };

  return (
    <header className="sm-navbar">
                <div className="sm-announce">
            <div className="sm-announce-inner">
              <span><FaShieldAlt /> 35 साल का भरोसा</span>
              <span><FaUsers /> लाखों मरीजों का विश्वास</span>
              <span><FaTruck /> सही दवा, सुरक्षित डिलीवरी</span>
            </div>
          </div>

      {/* ✅ ALWAYS VISIBLE: Logo + Profile + Cart */}
      <div className="sm-header">
        <Link to="/" className="sm-logo-wrap">
          <img src={logo} alt="Swasthmitra" className="sm-logo" />
        </Link>

        <div className="sm-header-icons">
          {isLoggedIn ? (
            <div className="sm-user-menu">
              <button
                className="sm-icon-btn1"
                onClick={() => setDropdownOpen((o) => !o)}
              >
                <FaUser size={20} />
                <span>प्रोफ़ाइल</span>
              </button>

              {dropdownOpen && (
                <div className="sm-dropdown">
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      navigate("/user-dashboard");
                    }}
                  >
                    Profile
                  </button>
                  <button onClick={handleLogout}>Logout</button>
                </div>
              )}
            </div>
          ) : (
            <button
              className="sm-icon-btn"
              onClick={() => navigate("/login")}
            >
              <FaUser size={20} />
              <span>लॉगिन</span>
            </button>
          )}

          <button className="sm-icon-btn" onClick={() => navigate("/cart")}>
            <div className="sm-cart-wrap">
              <FaCartPlus size={20} />
              {cart?.items?.length > 0 && (
                <span className="sm-cart-badge">{cart.items.length}</span>
              )}
            </div>
            <span>कार्ट</span>
          </button>
        </div>
      </div>

      {/* ✅ HOME PAGE ONLY: Announce bar + Search */}
      {isHomePage && (
        <>

          <div className="sm-search-wrap" ref={searchContainerRef}>
            <div className="sm-search">
              <FiSearch className="sm-search-icon" size={18} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={handleSearchInput}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                onFocus={() => setShowSuggestions(true)}
              />

              {searchTerm === "" && (
                <div
                  className="sm-search-placeholder"
                  onClick={() => searchInputRef.current?.focus()}
                >
                  Search&nbsp;
                  <span key={placeholderIndex} className="sm-anim-text">
                    {searchSuggestions[placeholderIndex]}
                  </span>
                </div>
              )}
            </div>

            {showSuggestions && suggestions.length > 0 && (
              <div className="sm-suggestions">
                {suggestions.map((s) => (
                  <div
                    key={s._id}
                    className="sm-suggestion"
                    onClick={() => handleSuggestionClick(s._id)}
                  >
                    <img
                      src={s.imageUrl || s.imageUrls?.[0] || df}
                      alt={s.name}
                    />
                    <div>
                      <div className="sm-sug-name">{s.name}</div>
                      <div className="sm-sug-info">
                        {s.brand && <span>{s.brand}</span>}
                        {s.salt && <span className="sm-sug-salt">{s.salt}</span>}
                        <span className="sm-sug-price">₹{s.mrp}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

    </header>
  );
};

export default Navbar;